import { Card, CompartmentCard } from "@/src/components/cards";
import {
  closeDrawer,
  openConfirmDialog,
  openDrawer,
} from "@/src/components/dialogs";
import {
  FetchableList,
  ListConstructor,
} from "@/src/components/fetchableLists";
import { IconTab } from "@/src/components/iconTab";
import StaggeredAnimation from "@/src/components/staggeredAnimationComponent";
import {
  Bed,
  Billet,
  entryModes,
  exitModes,
  Hospitalisation,
  Patient,
  Service,
  StructureUnit,
} from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import {
  getHospitalId,
  getItemId,
  getUserId,
  getUserShortName,
  openToast,
  uid,
} from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import AdmissionDocumentsList, {
  openAdmissionDocumentsList,
} from "../admission/admissionDocumentsList";
import { HospitalisationBilletForm, openBillet } from "../admission/billet";
import { ServicesChoice } from "../hospitalisations/servicesChoice";
import { getStandardConfidentiality } from "./confidentialitiesHook";
import { openAdmissionBedPicker } from "./admissionBedPicker";
import { FieldAnswersList } from "../fields/fieldsAnswersList";
import { InfoGrid } from "@/src/components/infoGrid";

export function BureauAdmissionsHospitalisationsHistory({
  patient,
}: {
  patient: Patient;
}) {
  const [reload, setReload] = useState(0);
  return (
    <CompartmentCard
      title={"Hospitalisations"}
      color="blue"
      onClick={async () => {
        await openAdmissionHospitalisationItem(
          null,
          patient,
          async (hospitalisation) => {
            setReload((prev) => prev + 1);
          }
        );
      }}
    >
      <FetchableList
        queryKey={["HospitalisationsHistory", patient.id, reload]}
        queryFn={() =>
          apiFetch("hospitalisations", "loadMany", {
            patientId: patient.id,
            bedsIds: null,
            additionalFetchArray: ["bed.unit", "bed.unit.service"],
          })
        }
      >
        {(data) => (
          <div className="grid c1 gap10">
            {data?.map((item, index) => {
              return (
                <StaggeredAnimation key={uid()} index={index}>
                  <HistoryItem
                    hospitalisation={item}
                    patient={patient}
                    isDeletable={true}
                    onConfirm={(item) => {
                      setReload((prev) => prev + 1);
                    }}
                  />
                  {/* <ConfidentialityCard
                  item={item}
                  onChange={async () => {
                    setReload((prev) => prev + 1);;
                  }}
                > */}

                  {/* </ConfidentialityCard> */}
                </StaggeredAnimation>
              );
            })}
          </div>
        )}
      </FetchableList>
    </CompartmentCard>
  );
}

function HistoryItem({
  hospitalisation,
  patient,
  isDeletable,
  onConfirm,
}: {
  hospitalisation: Hospitalisation;
  patient: Patient;
  isDeletable: boolean;
  onConfirm: (hospitalisation: Hospitalisation) => void;
}) {
  return (
    <Card
      title={"Hospitalisation"}
      subTitle={getUserShortName(hospitalisation?.creator)}
      color="blue"
      onClick={async () => {
        await openAdmissionDocumentsList({
          hospitalisation,
          patient,
          onConfirm,
        });
      }}
      onDelete={
        isDeletable == true
          ? async () => {
              await openConfirmDialog({
                onConfirm: async () => {
                  await apiFetch("general", "deleteAny", {
                    id: hospitalisation?.id,
                    table: "hospitalisations",
                  });
                  onConfirm?.(hospitalisation);
                },
              });
            }
          : undefined
      }
    >
      <div className="bold">
        {hospitalisation?.bed?.unit?.service?.name} -{" "}
        {hospitalisation?.bed?.unit?.name} - {hospitalisation?.bed?.name || ""}
      </div>

      <div className=" c1 gap5 mtop10">
        <div className="c2 gap10">
          <div className="gray">Mode d&apos;entrée</div>
          <div className="bold right">
            {entryModes[hospitalisation.entryMode!]?.name || ""}
          </div>
        </div>
        {hospitalisation.discharge?.exitMode && (
          <div className="c2 gap10">
            <div className="gray">Mode de sortie</div>
            <div className="bold right">
              {exitModes[hospitalisation.discharge?.exitMode]?.name}
            </div>
          </div>
        )}
      </div>

      <div className="mtop5">
        Entrée le {moment(hospitalisation._date).format("LL HH:mm")}
      </div>

      {hospitalisation.discharge?.exitDate && (
        <div>
          Sortie le{" "}
          {moment(hospitalisation.discharge.exitDate).format("LL HH:mm")}
        </div>
      )}
    </Card>
  );
}

export async function openAdmissionHospitalisationItem(
  hospitalisation: Hospitalisation | null,
  patient: Patient,
  onConfirm: (item: Hospitalisation) => void
) {
  if (!hospitalisation) {
    hospitalisation = {
      id: `hospitalisations:${uid()}`,
      type: "hospitalisation",
      decisionDoctor: null,
      patient,
      bed: undefined,
      confidentiality: getStandardConfidentiality(patient),
      readBy: [],
      status: "inProgress",
      creator: getUserId(),
      discharge: {
        exitDate: null,
        status: "finished",
        exitMode: "authorized",
        dischargeType: "immediate",
        type: "discharge",
        notes: "",
        parent: `hospitalisations:${uid()}` as any,
        creator: getUserId(),
        patient,
        confidentiality: getStandardConfidentiality(patient),
        readBy: [],
      },
      _date: moment().format("YYYY-MM-DD HH:mm"),
    };
    hospitalisation = await openHospitalisationServiceChoice(
      hospitalisation,
      async (hospitalisation) => {
        hospitalisation.status = "finished";

        hospitalisation = await apiFetch("hospitalisations", "addOne", {
          hospitalisation,
        });

        let item: Billet = {
          type: "billet",
          patient,
          creator: getUserId(),
          parent: hospitalisation,
          functionalSigns: [],
          physicalSigns: [],
          readBy: [getUserId()],
          confidentiality: getStandardConfidentiality(patient),
        };

        return await openBillet({
          item,
          parent: hospitalisation,
          patient,
          onChange: (item) => {
            onConfirm(hospitalisation);
          },
        });
      }
    );
  }
}

async function openHospitalisationBilletForm(
  hospitalisation: Hospitalisation,
  onConfirm: (hospitalisation: Hospitalisation) => void
) {
  await openDrawer({
    id: "statusChoiceDrawer",
    title: "Informations sur l'hospitalisation",
    content: (
      <div className="c1 gap10">
        <HospitalisationBilletForm
          current={hospitalisation}
          onChange={(newValue) => {
            hospitalisation = newValue;
          }}
        />
      </div>
    ),
    onConfirm: async () => {
      onConfirm(hospitalisation);
    },
  });
}

export async function openHospitalisationServiceChoice(
  hospitalisation: Hospitalisation,
  onConfirm: (hospitalisation: Hospitalisation) => void
) {
  await openDrawer({
    id: "serviceChoiceDrawer",
    title: "Choisir un service",
    content: (
      <div>
        <ServicesChoice
          types={["hospitalisations", "emergencies"]}
          current={null}
          hospitalId={getHospitalId()}
          onChange={async (service) => {
            closeDrawer("serviceChoiceDrawer");
            const units =
              service?.units?.filter(
                (unit) => unit?.type == "hospitalisation"
              ) || [];

            hospitalisation.bed = units?.[0]?.beds?.[0]!;
            // await openAdmissionBedPicker({
            //   service,
            //   enableAll: hospitalisation.status == "finished",
            //   onSelect: async (newBed) => {
            //     hospitalisation!.bed = newBed;
            
            onConfirm(hospitalisation);
            //   },
            // });
          }}
        />
      </div>
    ),
  });
  return hospitalisation;
}
